import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ShoppingCart, Truck, MagnifyingGlass } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS, whatsappLink } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { scrollToSection } from "@/lib/scroll";
import { getSearchQuery, setSearchQuery } from "@/lib/searchStore";
import { useProducts } from "@/lib/productsStore";
import { Logo } from "@/components/Logo";

const mxn = (n) => `$${n.toLocaleString("es-MX")} MXN`;

const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const normMap = (s) => {
  let out = "";
  const map = [];
  for (let i = 0; i < s.length; i++) {
    const n = normalize(s[i]);
    for (const ch of n) {
      out += ch;
      map.push(i);
    }
  }
  return { out, map };
};

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const { out, map } = normMap(text);
  const nq = normalize(q);
  const idx = out.indexOf(nq);
  if (idx === -1 || nq.length === 0) return text;
  const start = map[idx];
  const end = map[idx + nq.length - 1] + 1;
  return (
    <>
      {text.slice(0, start)}
      <strong className="font-bold">{text.slice(start, end)}</strong>
      {text.slice(end)}
    </>
  );
};

const SearchField = ({ mobile = false, query, onQuery, autoFocus = false }) => {
  const { products } = useProducts();
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const q = normalize(query).trim();
  const matches = q
    ? products
        .filter((p) =>
          [p.name, p.category, p.tagline, ...(p.specs || [])].some((f) => normalize(f).includes(q))
        )
        .slice(0, 6)
    : [];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setListOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goAll = () => {
    setListOpen(false);
    setActive(-1);
    if (location.pathname !== "/") navigate("/#equipamiento");
    scrollToSection("equipamiento");
  };

  const goProduct = (p) => {
    setListOpen(false);
    setActive(-1);
    navigate(`/producto/${p.id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, matches.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && active < matches.length) goProduct(matches[active]);
      else goAll();
    } else if (e.key === "Escape") {
      setListOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${mobile ? "w-full" : ""}`}>
      <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none z-10" />
      <input
        data-testid={mobile ? "header-search-mobile-input" : "header-search-input"}
        type="text"
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => {
          onQuery(e.target.value);
          setListOpen(true);
          setActive(-1);
        }}
        onFocus={() => query.trim() && setListOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Buscar productos"
        className={`${mobile ? "w-full h-11" : "w-48 xl:w-60 h-10"} rounded-full bg-white text-black text-sm pl-10 pr-4 outline-none placeholder:text-black/40`}
      />
      {listOpen && query.trim() && (
        <div
          data-testid="search-suggestions"
          data-lenis-prevent
          className={`absolute top-full mt-2 bg-white rounded-2xl border border-black/10 shadow-2xl z-[1101] max-h-[60vh] overflow-y-auto overscroll-contain ${
            mobile ? "left-0 right-0" : "right-0 w-[400px]"
          }`}
        >
          {matches.length === 0 ? (
            <p data-testid="search-no-match" className="px-5 py-4 text-sm text-black/50">
              No encontramos productos con ese nombre
            </p>
          ) : (
            <>
              {matches.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`search-suggestion-${p.id}`}
                  onClick={() => goProduct(p)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${active === i ? "bg-black/5" : ""}`}
                >
                  <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-contain bg-[#F6F7F8] shrink-0" />
                  <span className="flex-1 min-w-0 text-sm text-black truncate">
                    <Highlight text={p.name} q={query.trim()} />
                  </span>
                  <span className="text-sm font-bold text-black shrink-0">{mxn(p.price_mxn)}</span>
                </button>
              ))}
              <button
                data-testid="search-view-all"
                onClick={goAll}
                onMouseEnter={() => setActive(matches.length)}
                className={`w-full px-5 py-3 text-left text-sm font-semibold text-[#FF5508] border-t border-black/10 transition-colors ${active === matches.length ? "bg-black/5" : ""}`}
              >
                Ver todos los resultados para "{query.trim()}"
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(() => !!getSearchQuery());
  const [query, setQuery] = useState(getSearchQuery());
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate(href);
      } else {
        window.history.replaceState(null, "", href);
      }
      scrollToSection(id);
      return;
    }
    navigate(href);
  };

  const onQuery = (val) => {
    setQuery(val);
    setSearchQuery(val);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-[1000]"
    >
      <div data-testid="promo-bar" className="bg-[#FF5508] text-white">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-center gap-2 text-[10px] sm:text-[11px] text-center">
          <Truck size={14} weight="bold" className="shrink-0" />
          <span>Envío gratis en compras mayores a $1,399 MXN</span>
          <span className="hidden md:inline text-white/70">· 10% en tu primera compra: déjanos tu correo</span>
        </div>
      </div>
      <div className="bg-black border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <button
            data-testid="logo-button"
            onClick={() => go("/#inicio")}
            className="flex items-center gap-3 group shrink-0"
          >
            <Logo className="h-9 w-9 rounded-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <span className="font-display text-2xl font-semibold tracking-wide uppercase text-white">
              Ruta<span className="text-[#FF5508]">360</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-${link.href.replace(/[/#]/g, "")}`}
                onClick={() => go(link.href)}
                className="text-[13px] text-white/60 transition-colors duration-300 hover:text-white"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:block shrink-0">
            <SearchField query={query} onQuery={onQuery} />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              data-testid="header-search-toggle"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Buscar productos"
              className="lg:hidden text-white p-2 hover:text-[#FF5508] transition-colors duration-300"
            >
              <MagnifyingGlass size={21} />
            </button>
            <button
              data-testid="header-cart-button"
              onClick={() => setCartOpen(true)}
              aria-label="Abrir carrito"
              className="relative text-white p-2 hover:text-[#FF5508] transition-colors duration-300"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#FF5508] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <a
              data-testid="header-whatsapp-cta"
              href={whatsappLink("Hola Ruta 360, quiero información sobre su equipo de exploración.")}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] text-white text-[11px] px-5 py-2.5 rounded-full transition-colors duration-300 hover:bg-white hover:text-black"
            >
              WhatsApp
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-white p-2"
              aria-label="Abrir menú"
            >
              {open ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="lg:hidden border-t border-white/10 px-6 py-3">
            <SearchField mobile autoFocus query={query} onQuery={onQuery} />
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`mobile-nav-${link.href.replace(/[/#]/g, "")}`}
                  onClick={() => go(link.href)}
                  className="text-left font-display text-3xl uppercase text-white/80 hover:text-[#FF5508] transition-colors duration-300"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
